import dagre from '@dagrejs/dagre';
import { Node, Edge, Position } from '@xyflow/react';
import type { DagNode, SeederInstance } from '../types/sales-demo.types';
import { deriveDatasetName } from './dag-builder';

export type DagNodeData = {
  label: string;
  typeUrl: string;
  seeders: SeederInstance[];
  healthyCount: number;
  unhealthyCount: number;
  totalCount: number;
  aggregateStatus: 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY' | 'UNREGISTERED';
  dependencyCount: number;
  hasUnhealthyUpstream: boolean;
};

const NODE_WIDTH = 240;
const NODE_HEIGHT = 100;

export function buildFlowGraph(dagNodes: DagNode[]): { nodes: Node<DagNodeData>[]; edges: Edge[] } {
  const registeredTypes = new Set(dagNodes.map((n) => n.typeUrl));

  const virtualNodes: DagNode[] = [];
  for (const n of dagNodes) {
    for (const dep of n.dependencies) {
      if (!registeredTypes.has(dep) && !virtualNodes.some((v) => v.typeUrl === dep)) {
        virtualNodes.push({ id: dep, typeUrl: dep, dependencies: [], seeders: [] });
      }
    }
  }

  const allDagNodes = [...dagNodes, ...virtualNodes];
  const allNodeMap = new Map(allDagNodes.map((n) => [n.typeUrl, n]));
  const unhealthyUpstream = computeUnhealthyUpstream(allDagNodes);

  const nodes: Node<DagNodeData>[] = allDagNodes.map((n) => {
    const healthyCount = n.seeders.filter((s) => s.status === 'HEALTHY').length;
    const unhealthyCount = n.seeders.filter((s) => s.status !== 'HEALTHY').length;
    const total = n.seeders.length;
    let aggregateStatus: DagNodeData['aggregateStatus'];
    if (total === 0) aggregateStatus = 'UNREGISTERED';
    else if (unhealthyCount === 0) aggregateStatus = 'HEALTHY';
    else if (healthyCount === 0) aggregateStatus = 'UNHEALTHY';
    else aggregateStatus = 'DEGRADED';

    return {
      id: n.typeUrl,
      type: 'dagNode',
      position: { x: 0, y: 0 },
      data: {
        label: deriveDatasetName(n.typeUrl),
        typeUrl: n.typeUrl,
        seeders: n.seeders,
        healthyCount,
        unhealthyCount,
        totalCount: total,
        aggregateStatus,
        dependencyCount: n.dependencies.length,
        hasUnhealthyUpstream: unhealthyUpstream.has(n.typeUrl),
      },
    };
  });

  const edges: Edge[] = [];
  for (const n of allDagNodes) {
    for (const depTypeUrl of n.dependencies) {
      if (!allNodeMap.has(depTypeUrl)) continue;
      const sourceData = nodes.find((nd) => nd.id === depTypeUrl)?.data;
      const isUnhealthyPath = sourceData
        ? sourceData.aggregateStatus !== 'HEALTHY' || unhealthyUpstream.has(depTypeUrl)
        : true;
      edges.push({
        id: `${depTypeUrl}->${n.typeUrl}`,
        source: depTypeUrl,
        target: n.typeUrl,
        type: 'default',
        animated: isUnhealthyPath,
        style: {
          stroke: isUnhealthyPath ? '#ff9800' : '#90a4ae',
          strokeWidth: 2,
          strokeDasharray: isUnhealthyPath ? '6 3' : undefined,
        },
        markerEnd: {
          type: 'arrowclosed' as any,
          color: isUnhealthyPath ? '#ff9800' : '#90a4ae',
        },
      });
    }
  }

  return { nodes, edges };
}

function computeUnhealthyUpstream(dagNodes: DagNode[]): Set<string> {
  const unhealthy = new Set<string>();
  const dependents = new Map<string, string[]>();
  for (const n of dagNodes) {
    for (const depTypeUrl of n.dependencies) {
      if (!dependents.has(depTypeUrl)) dependents.set(depTypeUrl, []);
      dependents.get(depTypeUrl)!.push(n.typeUrl);
    }
  }

  const queue = dagNodes
    .filter((n) => n.seeders.length === 0 || n.seeders.some((s) => s.status !== 'HEALTHY'))
    .map((n) => n.typeUrl);
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const downstream of dependents.get(current) ?? []) {
      if (!unhealthy.has(downstream)) {
        unhealthy.add(downstream);
        queue.push(downstream);
      }
    }
  }
  return unhealthy;
}

export function applyDagreLayout(nodes: Node<DagNodeData>[], edges: Edge[]): Node<DagNodeData>[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 50 });
  for (const node of nodes) g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  for (const edge of edges) g.setEdge(edge.source, edge.target);
  dagre.layout(g);
  return nodes.map((node) => {
    const pos = g.node(node.id);
    return {
      ...node,
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  });
}
